/**
 * layerSections — ORDER-PRESERVING per-layer segmentation of a CadPrimitive
 * list (PDF-6.2a infrastructure for OCG / layer-aware paint).
 *
 * Given an ORDERED primitive list (authored draw order), this groups
 * CONSECUTIVE same-layer primitives into one section, PRESERVING the order. It
 * is deliberately NOT a "bucket by layer" — that would reorder an A/B/A draw
 * sequence into A,A,B and break the z-order of overlapping / filled geometry
 * (the same draw-order constraint `batchPaint.emitBatchedPaths` honours with
 * run-length style batching, #177 P2). An A,B,A layer sequence therefore yields
 * THREE sections `[A, B, A]`, not two.
 *
 * This is the structure PDF-6.2b will wrap in PDF marked-content (`BDC … EMC`)
 * against `/OCProperties` optional-content groups: one BDC/EMC pair per section
 * so each consecutive same-layer run paints inside its layer's OCG while the
 * authored operator order is untouched.
 *
 * # PDF-6.2a constraint (byte-identity)
 * This module is PURE and is NOT yet wired into the emit path. 6.2a leaves the
 * emitted operator stream byte-identical; `emitBatchedPaths` is unchanged and
 * does not consume {@link layerSections}. 6.2b wires it. The function is
 * exported + unit-tested so 6.2b can build on a proven segmentation.
 */

import type { CadPrimitive } from "./cadPrimitive";

/**
 * One ordered run of consecutive same-layer primitives. `items` keeps the
 * authored order within the run; the SECTION list keeps the authored order
 * across runs (so A,B,A → three sections, the first and last both `layer: "A"`
 * but distinct objects).
 */
export interface LayerSection {
  /** The OCG/drawing layer every primitive in this run files under. */
  layer: string;
  /** The consecutive same-layer primitives, in authored order. */
  items: CadPrimitive[];
}

/**
 * Segment an ORDERED primitive list into ordered {@link LayerSection}s where
 * each section is a maximal run of CONSECUTIVE same-layer primitives.
 *
 * Order-preserving (NOT bucketed):
 *   - `[]` → `[]`;
 *   - all-one-layer → a single section;
 *   - `A,A,B,A` → `[{A:[a,a]}, {B:[b]}, {A:[a]}]` (THREE sections);
 *   - `A,B,B`   → `[{A:[a]}, {B:[b,b]}]` (TWO sections).
 *
 * A new section starts whenever the next primitive's `layer` differs from the
 * open run's layer (strict `!==` on the exact stored name — case is preserved
 * as authored, matching how the converters stamp the resolved layer). The
 * returned `items` arrays are fresh; the primitive objects are shared by
 * reference (no copy — 6.2b only needs to know the grouping + order).
 */
export function layerSections(
  primitives: readonly CadPrimitive[],
): LayerSection[] {
  const sections: LayerSection[] = [];
  let current: LayerSection | null = null;
  for (const prim of primitives) {
    if (current === null || current.layer !== prim.layer) {
      current = { layer: prim.layer, items: [prim] };
      sections.push(current);
    } else {
      current.items.push(prim);
    }
  }
  return sections;
}
